import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import './SettingsDetailPage.css';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import { CheckedCombo } from '../../components/CheckedCombo';
import { ExportMenu } from '../../components/AppToolbar';
import type { CrudField, ModuleCrudConfig } from './moduleCrud';

interface Option { id: string; name: string }

/** The subset of a ModuleCrudConfig this detail view needs.  A full
 *  ModuleCrudConfig satisfies it (ModuleListView path); a bespoke page supplies
 *  just these four + an `adapter` for its own data layer.  `basePath` is only
 *  required when NO adapter is given (the default REST path). */
export type DetailConfig =
  Pick<ModuleCrudConfig,
    'fields' | 'formName' | 'entityLabel' | 'idField'
    | 'detailHideEdit' | 'detailShowDelete' | 'detailOpenInEdit' | 'singleColumn' | 'titleField'
    | 'hideSectionHeader'>
  & { basePath?: string };

/** Optional data-layer override so a bespoke page (TanStack mutations, custom
 *  endpoints, …) can reuse this exact detail view instead of the REST basePath.
 *  When omitted, the view uses `api.get/post/put` against `config.basePath`. */
export interface DetailDataAdapter {
  /** Load one record's field values for Edit (keyed by CrudField.key). */
  load: (id: string) => Promise<Record<string, unknown>>;
  /** Persist a new record; return its id if known (so Save can stay on it), else null. */
  create: (payload: Record<string, unknown>) => Promise<string | null>;
  /** Persist an existing record. */
  update: (id: string, payload: Record<string, unknown>) => Promise<void>;
}

interface Props {
  config: DetailConfig;
  /** Optional bespoke data layer; when omitted, REST via `config.basePath`. */
  adapter?: DetailDataAdapter;
  /** null = New (create); a record id = Edit. */
  id: string | null;
  /** How the record should OPEN — 'view' (read-only, row-click) or 'edit'
   *  (editable, Edit action). Defaults to 'view' for an existing record, 'edit'
   *  for New. Lets the list distinguish row-click (view) from the Edit pencil. */
  initialMode?: 'view' | 'edit';
  /** Form title (nav label) + breadcrumb group, sourced from ModuleListView. */
  title: string;
  group: string;
  /** Ordered ids of the current list page — powers the prev/next arrows. */
  siblingIds?: string[];
  onHome: () => void;
  /** Jump to another record (Edit) — used by the prev/next arrows. */
  onNavigate: (id: string) => void;
  /** Return to the list view (Cancel / Save & Close). */
  onClose: () => void;
  /** Refresh the underlying list after a successful save. */
  onSaved: () => void;
}

/** After-save behaviour for the Save trio. */
type AfterSave = 'stay' | 'new' | 'close';

/** ISO / date string → yyyy-MM-dd for <input type=date>. */
function toDateInput(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Best-effort extract of the created record id from a POST response so
 *  "Save" on a New record can switch to edit mode instead of duplicating it. */
function createdIdFrom(resData: unknown, idField: string): string | null {
  const body = (resData as { data?: unknown })?.data;
  if (typeof body === 'string' && body) return body;
  if (body && typeof body === 'object') {
    const v = (body as Record<string, unknown>)[idField];
    if (v != null && v !== '') return String(v);
  }
  return null;
}

/** Empty (New) form values from the config's field defaults. */
function emptyValues(config: DetailConfig): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  for (const f of config.fields) {
    v[f.key] = f.defaultValue !== undefined
      ? f.defaultValue
      : (f.type === 'checkbox' ? false
        : (f.type === 'multiselect' || f.type === 'assocgrid') ? [] : '');
    // A 'file' field also tracks its display name in a sibling key.
    if (f.type === 'file' && f.fileNameKey) v[f.fileNameKey] = '';
  }
  return v;
}

/**
 * The single shared Settings-BO Detail View (Class A/B).  Driven entirely by
 * the same ModuleCrudConfig the list uses, so every CRUD-enabled settings form
 * gets the identical detail screen for free: header + breadcrumb + prev/next,
 * action bar top & bottom, and the Save trio (Save / Save & New / Save & Close)
 * through the shared useSaveFlow (confirm → audited handler → "Saved
 * successfully."). Never bypasses the API handler.
 */
export function SettingsDetailPage({
  config, adapter, id, initialMode, title, group, siblingIds = [], onHome, onNavigate, onClose, onSaved,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  // currentId is seeded from the prop but can change locally (Save on a New
  // record → edit the just-created row; Save & New → back to null).
  const [currentId, setCurrentId] = useState<string | null>(id);
  const editing = currentId !== null;
  // ALPACA parity (BO UI Standard v2, 2026-07-02): opening an EXISTING record
  // from the list shows the detail READ-ONLY first ('view' — Edit/Close action
  // bar); New goes straight to 'edit'.  Edit switches the SAME detail to edit.
  const [mode, setMode] = useState<'view' | 'edit'>(
    initialMode ?? (id !== null ? (config.detailOpenInEdit ? 'edit' : 'view') : 'edit'));
  const viewOnly = mode === 'view';
  const [values, setValues] = useState<Record<string, unknown>>(() => emptyValues(config));
  const [options, setOptions] = useState<Record<string, Option[]>>({});
  const [loading, setLoading] = useState(id !== null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triedSave, setTriedSave] = useState(false);

  // Cascade wiring: map each PARENT field key -> the child field keys that
  // declare `dependsOn` it, so changing a parent can clear its descendants.
  const childrenOf = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const f of config.fields) if (f.dependsOn) (m[f.dependsOn] ??= []).push(f.key);
    return m;
  }, [config]);

  const set = (k: string, v: unknown) => setValues((s) => {
    if (s[k] === v) return { ...s, [k]: v };
    const next = { ...s, [k]: v };
    // Clear dependent descendants when a parent changes (legacy cascade reset).
    const clear = (key: string) => {
      for (const childKey of childrenOf[key] ?? []) {
        const cf = config.fields.find((f) => f.key === childKey);
        next[childKey] = cf?.type === 'multiselect' ? [] : '';
        clear(childKey);
      }
    };
    clear(k);
    return next;
  });

  // Keep the local id in step when the parent swaps the record (prev/next).
  // Navigating between records (or re-opening) always lands in view mode for
  // an existing record, edit mode for New.
  useEffect(() => {
    setCurrentId(id);
    setMode(initialMode ?? (id !== null ? (config.detailOpenInEdit ? 'edit' : 'view') : 'edit'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialMode]);

  // Load select options once per config.  A field carrying a STATIC `options`
  // list is seeded straight away (no request); only `optionsUrl` fields are
  // fetched.  Statics are applied first so the control is never briefly empty.
  useEffect(() => {
    let alive = true;
    const statics = config.fields.filter((f) => f.type === 'select' && !f.optionsUrl && f.options);
    if (statics.length > 0) {
      setOptions((prev) => ({ ...prev, ...Object.fromEntries(statics.map((f) => [f.key, f.options!])) }));
    }
    // Independent (non-cascade) option lists load once; cascade fields with
    // `dependsOn` are loaded by the effect below, keyed on their parent's value.
    const selects = config.fields.filter(
      (f) => (f.type === 'select' || f.type === 'multiselect') && f.optionsUrl && !f.dependsOn);
    if (selects.length === 0) return () => { alive = false; };
    Promise.all(
      selects.map(async (f) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(f.optionsUrl!);
          return [f.key, data.data ?? []] as const;
        } catch {
          return [f.key, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => { if (alive) setOptions((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { alive = false; };
  }, [config]);

  // Cascade: (re)load each dependent field's options from
  // `optionsUrl?{dependsOn}={parentValue}` whenever its parent value changes —
  // e.g. Bottle Sharing's Matrix (by Sample Matrix) and Test (by Matrix). An
  // empty parent yields an empty list. Runs on edit-prefill too, so the loaded
  // child values resolve to labels. `depSig` is the signature of parent values.
  const dependents = useMemo(
    () => config.fields.filter(
      (f) => (f.type === 'select' || f.type === 'multiselect') && !!f.optionsUrl && !!f.dependsOn),
    [config],
  );
  const depSig = dependents.map((f) => `${f.key}=${String(values[f.dependsOn!] ?? '')}`).join('|');
  useEffect(() => {
    if (dependents.length === 0) return;
    let alive = true;
    Promise.all(
      dependents.map(async (f) => {
        const raw = values[f.dependsOn!];
        const parentVal = raw == null ? '' : String(raw);
        if (parentVal === '') return [f.key, [] as Option[]] as const;
        try {
          const url = `${f.optionsUrl}?${encodeURIComponent(f.dependsOn!)}=${encodeURIComponent(parentVal)}`;
          const { data } = await api.get<{ data?: Option[] }>(url);
          return [f.key, data.data ?? []] as const;
        } catch {
          return [f.key, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => { if (alive) setOptions((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depSig, dependents]);

  // Edit: prefill via the adapter (bespoke) or GET base/{id} (REST).  New: empty.
  useEffect(() => {
    setError(null); setTriedSave(false);
    if (currentId === null) { setValues(emptyValues(config)); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    const fetchRecord = async (): Promise<Record<string, unknown>> => {
      if (adapter) return adapter.load(currentId);
      const res = await api.get<{ data?: Record<string, unknown> }>(`${config.basePath}/${currentId}`);
      return res.data.data ?? {};
    };
    fetchRecord()
      .then((d) => {
        if (!alive) return;
        const next: Record<string, unknown> = {};
        for (const f of config.fields) {
          const raw = d[f.key];
          if (f.type === 'date') next[f.key] = toDateInput(raw);
          else if (f.type === 'checkbox') next[f.key] = Boolean(raw);
          else if (f.type === 'multiselect' || f.type === 'assocgrid') next[f.key] = Array.isArray(raw) ? raw.map(String) : [];
          else if (f.type === 'file') {
            next[f.key] = raw == null ? '' : String(raw); // FileData Oid
            if (f.fileNameKey) next[f.fileNameKey] = d[f.fileNameKey] == null ? '' : String(d[f.fileNameKey]);
          }
          else next[f.key] = raw == null ? '' : String(raw);
        }
        setValues(next);
      })
      .catch(() => { if (alive) setError(`Could not load this ${config.entityLabel.toLowerCase()}.`); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [config, adapter, currentId]);

  const fieldEmpty = (f: CrudField) =>
    f.type === 'multiselect'
      ? !Array.isArray(values[f.key]) || (values[f.key] as unknown[]).length === 0
      : String(values[f.key] ?? '').trim() === '';
  const missingRequired = useMemo(
    () => config.fields.filter((f) => f.required && f.type !== 'readonly' && fieldEmpty(f)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.fields, values],
  );
  const isEmpty = (f: CrudField) => !!f.required && f.type !== 'readonly' && fieldEmpty(f);

  // Prev/next within the current list page.
  const idx = currentId ? siblingIds.indexOf(currentId) : -1;
  const prevId = idx > 0 ? siblingIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : null;

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const f of config.fields) {
      if (f.type === 'readonly') continue;
      const v = values[f.key];
      if (f.type === 'checkbox') payload[f.key] = Boolean(v);
      else if (f.type === 'multiselect' || f.type === 'assocgrid') payload[f.key] = Array.isArray(v) ? v : [];
      else { const s = v == null ? '' : String(v).trim(); payload[f.key] = s === '' ? null : s; }
    }
    return payload;
  };

  const doSave = async (after: AfterSave) => {
    setTriedSave(true);
    if (missingRequired.length > 0) {
      setError(`Required: ${missingRequired.map((f) => f.label).join(', ')}.`);
      return;
    }
    setError(null);
    setSaving(true);
    const wasEditing = editing;
    let createdId: string | null = null;
    // Shared save flow: confirm → audited handler (POST/PUT) → "Saved
    // successfully." Create is reason-exempt; Edit gates the reason (rule 11).
    await runSave({
      formName: config.formName,
      auditAction: wasEditing ? 'edit' : 'create',
      successMessage: `${config.entityLabel} saved successfully.`,
      save: async () => {
        const payload = buildPayload();
        if (wasEditing) {
          if (adapter) await adapter.update(currentId!, payload);
          else await api.put(`${config.basePath}/${currentId}`, payload);
        } else if (adapter) {
          createdId = await adapter.create(payload);
        } else {
          const res = await api.post(config.basePath!, payload);
          createdId = createdIdFrom(res.data, config.idField);
        }
      },
      onSaved: () => {
        onSaved(); // refresh the list behind the detail
        if (after === 'close') { onClose(); return; }
        if (after === 'new') { setCurrentId(null); setValues(emptyValues(config)); setTriedSave(false); setMode('edit'); return; }
        // 'stay' — remain on the saved record. Back to read-only VIEW mode,
        // unless the form always opens editable (detailOpenInEdit), matching the
        // legacy XAF detail that stays editable after Save.
        const afterMode = config.detailOpenInEdit ? 'edit' : 'view';
        if (!wasEditing) {
          if (createdId) { setCurrentId(createdId); setMode(afterMode); } // reload
          else onClose(); // couldn't resolve the new id → safest: return to list
        } else {
          setMode(afterMode);
        }
      },
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Save failed.';
        setError(msg);
        notify.error(msg);
      },
    });
    setSaving(false);
  };

  // Header record name = the caption field's value (config.titleField, mirroring
  // the legacy XAF [DefaultProperty]; falls back to the first field). For a FK
  // 'select' caption field (e.g. Bottle Sharing's Sample Matrix, Assign
  // Dashboard's Dashboard View), resolve the stored id → its option label so the
  // header/breadcrumb reads the name, not a raw GUID. When titleField is set the
  // caption shows even on a NEW record once the field has a value.
  const captionField = config.fields.find((f) => f.key === config.titleField) ?? config.fields[0];
  const captionVal = values[captionField?.key ?? ''];
  const captionDisplay = captionField?.type === 'select'
    ? ((options[captionField.key] ?? []).find((o) => o.id === String(captionVal ?? ''))?.name ?? '')
    : String(captionVal ?? '');
  const recordName = editing
    ? (captionDisplay || config.entityLabel)
    : (config.titleField && captionDisplay ? captionDisplay : `New ${config.entityLabel}`);

  // Detail toolbar is config-driven (per-form): Edit shows unless
  // detailHideEdit; Delete shows for an existing REST record when
  // detailShowDelete. Delete uses the shared flow (danger confirm → reason gate
  // → soft delete → "Deleted.") then returns to the list.
  const showEdit = !config.detailHideEdit;
  const canDelete = !!config.detailShowDelete && editing && !!config.basePath && !adapter;
  const doDelete = async () => {
    if (!canDelete || currentId === null) return;
    await runDelete({
      formName: config.formName,
      message: `Delete ${recordName}? This cannot be undone.`,
      delete: async () => { await api.delete(`${config.basePath}/${currentId}`); },
      onDeleted: () => { onSaved(); onClose(); },
    });
  };

  // Glyphs = the EXACT BO UI Standard prototype .abtn icons (💾 💾 📄 ↩︎).
  // Save action set — XAF-canonical order & wording (BO UI Standard §4, decision
  // 2026-07-08): Save · Save and Close · Save and New · | · Cancel. Words spelled
  // "and" (never "&"), a thin divider precedes Cancel (XAF parity).
  // VIEW mode (ALPACA parity): Edit (blue ✎) + Close — no Save/Cancel.
  const ActionBar = ({ bottom }: { bottom?: boolean }) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {viewOnly ? (
        <>
          {showEdit && (
            <button className="sd-abtn primary" onClick={() => setMode('edit')} disabled={loading}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
          )}
          {canDelete && (
            <button className="sd-abtn danger" onClick={doDelete} disabled={loading}>
              <span className="tb-i tb-i-red">🗑</span> Delete
            </button>
          )}
          <button className="sd-abtn cancel" onClick={onClose}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      ) : (
        <>
          <button className="sd-abtn primary" onClick={() => doSave('stay')} disabled={saving || loading}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="sd-abtn" onClick={() => doSave('close')} disabled={saving || loading}>
            <span className="tb-i">💾</span> Save and Close
          </button>
          <button className="sd-abtn" onClick={() => doSave('new')} disabled={saving || loading}>
            <span className="tb-i">📄</span> Save and New
          </button>
          {canDelete && (
            <button className="sd-abtn danger" onClick={doDelete} disabled={saving || loading}>
              <span className="tb-i tb-i-red">🗑</span> Delete
            </button>
          )}
          <span className="sd-abtn-sep" aria-hidden="true" />
          <button className="sd-abtn cancel" onClick={onClose} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          {/* Header — title + breadcrumb + prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>{config.entityLabel} – {recordName}</h2>
              <div className="sd-bc">
                <a onClick={onHome}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>{group}</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose}>{title}</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button className="sd-na" title="Previous record" disabled={!prevId || saving}
                onClick={() => prevId && onNavigate(prevId)}>‹</button>
              <button className="sd-na next" title="Next record" disabled={!nextId || saving}
                onClick={() => nextId && onNavigate(nextId)}>›</button>
            </div>
          </div>

          <ActionBar />

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading"><LoaderSymbol size={20} /> Loading…</div>
            ) : (
              <>
                {!config.hideSectionHeader && (
                  <h3 className="sd-section-h">General Information</h3>
                )}
                <div className={`sd-grid${config.singleColumn ? ' sd-grid--single' : ''}`}>
                  {config.fields.filter((f) => f.type !== 'assocgrid').map((f) => (
                    f.type === 'file' ? (
                      <FileField key={f.key} field={f} readOnly={viewOnly}
                        oid={values[f.key] == null ? '' : String(values[f.key])}
                        fileName={f.fileNameKey ? String(values[f.fileNameKey] ?? '') : ''}
                        onChange={(oid, name) => setValues((s) => ({
                          ...s, [f.key]: oid, ...(f.fileNameKey ? { [f.fileNameKey]: name } : {}),
                        }))} />
                    ) : (
                      <Field key={f.key} field={f} value={values[f.key]}
                        options={options[f.key] ?? []}
                        showEmpty={!viewOnly && triedSave && isEmpty(f)}
                        readOnly={viewOnly}
                        onChange={(v) => set(f.key, v)} />
                    )
                  ))}
                </div>
                {/* Association grids (legacy child-collection tabs, e.g. Test
                    Methods Link/Unlink) render full-width below the main form. */}
                {config.fields.filter((f) => f.type === 'assocgrid').map((f) => (
                  <AssocGridField key={f.key} field={f}
                    value={Array.isArray(values[f.key]) ? (values[f.key] as string[]) : []}
                    readOnly={viewOnly}
                    onChange={(v) => set(f.key, v)} />
                ))}
                {error && (
                  <div className="sd-error"><i className="ti ti-alert-triangle" /> {error}</div>
                )}
              </>
            )}
          </div>

          <ActionBar bottom />
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, options, showEmpty, readOnly, onChange }: {
  field: CrudField; value: unknown; options: Option[]; showEmpty: boolean;
  /** VIEW mode — render the control disabled/greyed (same as type 'readonly'). */
  readOnly: boolean;
  onChange: (v: unknown) => void;
}) {
  const id = `sd-${field.key}`;
  const isArea = field.type === 'textarea';
  const ro = readOnly || field.type === 'readonly';
  const cls = `sd-input${showEmpty ? ' required-empty' : ''}${ro ? ' readonly' : ''}`;

  if (field.type === 'checkbox') {
    return (
      <div className="sd-row">
        <label className="sd-check" htmlFor={id}>
          <input id={id} type="checkbox" checked={Boolean(value)} disabled={ro}
            onChange={(e) => onChange(e.target.checked)} />
          {field.label}
        </label>
      </div>
    );
  }

  // Multi-select — a checked-list DROPDOWN (shared CheckedCombo), matching the
  // legacy XAF CheckedListBoxEditor. Options are {id,name}; the stored value is
  // an array of ids, so map id↔name at the control boundary.
  if (field.type === 'multiselect') {
    const ids = Array.isArray(value) ? (value as unknown[]).map(String) : [];
    const nameById = new Map(options.map((o) => [o.id, o.name] as const));
    const idByName = new Map(options.map((o) => [o.name, o.id] as const));
    const selectedNames = ids.map((i) => nameById.get(i)).filter((n): n is string => !!n);
    return (
      <div className="sd-row">
        <label className="sd-label">
          {field.label}{field.required ? <span className="req">*</span> : null}
        </label>
        <div className="sd-ms">
          <CheckedCombo
            options={options.map((o) => o.name)}
            values={selectedNames}
            onChange={(names) => onChange(names.map((n) => idByName.get(n)).filter((x): x is string => !!x))}
            disabled={ro}
            required={showEmpty}
            placeholder={field.noneLabel ?? 'Select…'}
            ariaLabel={field.label}
            pageSize={field.pageSize}
            summaryJoin={field.summarySeparator}
            hideOk={field.hideOk}
          />
        </div>
        {field.hint && <span className="sd-hint">{field.hint}</span>}
      </div>
    );
  }

  return (
    <div className={`sd-row${isArea ? ' full' : ''}`}>
      <label className="sd-label" htmlFor={id}>
        {field.label}{field.required ? <span className="req">*</span> : null}
      </label>
      {field.type === 'readonly' ? (
        <input id={id} className={cls} type="text" readOnly disabled
          placeholder={field.placeholder}
          value={value == null || value === ''
            ? (field.placeholder ? '' : '(auto)')
            : String(value)} />
      ) : field.type === 'select' ? (
        <select id={id} className={cls} value={value == null ? '' : String(value)} disabled={ro}
          onChange={(e) => onChange(e.target.value)}>
          <option value="">{field.noneLabel ?? '— None —'}</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      ) : field.type === 'date' ? (
        <input id={id} className={cls} type="date" value={value == null ? '' : String(value)}
          readOnly={ro} disabled={ro} onChange={(e) => onChange(e.target.value)} />
      ) : isArea ? (
        <textarea id={id} className={cls} rows={3} maxLength={field.maxLength} readOnly={ro} disabled={ro}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} className={cls} type="text" maxLength={field.maxLength} readOnly={ro} disabled={ro}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.hint && <span className="sd-hint">{field.hint}</span>}
    </div>
  );
}

interface AssocRow { id: string; name: string; methodName?: string; formulaCount?: number; isFormulaActive?: boolean }

/**
 * Association grid (shared 'assocgrid' CrudFieldType) — the legacy XAF
 * child-collection tab (e.g. DataPackageCheckList "Test Methods" Link/Unlink).
 * Self-fetches its rows from `field.optionsUrl` (each {id,name,methodName,
 * formulaCount,isFormulaActive}); the field value is the array of linked ids
 * (same wire shape as a multiselect). Link opens a picker of the not-yet-linked
 * rows; Unlink removes the checked rows. Read-only in view mode.
 */
function AssocGridField({ field, value, readOnly, onChange }: {
  field: CrudField; value: string[]; readOnly: boolean; onChange: (v: string[]) => void;
}) {
  const [rows, setRows] = useState<AssocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickOpen, setPickOpen] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [pickSel, setPickSel] = useState<Set<string>>(new Set());
  const [pickSearch, setPickSearch] = useState('');

  useEffect(() => {
    let alive = true;
    if (!field.optionsUrl) { setLoading(false); return; }
    api.get<{ data?: AssocRow[] }>(field.optionsUrl)
      .then((r) => { if (alive) setRows(r.data.data ?? []); })
      .catch(() => { /* leave empty; grid shows "No data" */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [field.optionsUrl]);

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const selectedRows = value.map((id) => byId.get(id)).filter((r): r is AssocRow => !!r);
  // Paging for the linked-rows grid (legacy footer: Page X of N + size dropdown).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.max(1, Math.ceil(selectedRows.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageRows = selectedRows.slice((curPage - 1) * pageSize, curPage * pageSize);
  const available = rows.filter((r) => !value.includes(r.id));
  const q = pickSearch.trim().toLowerCase();
  const availFiltered = q
    ? available.filter((r) => `${r.name} ${r.methodName ?? ''}`.toLowerCase().includes(q))
    : available;

  const toggleSel = (id: string) =>
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePick = (id: string) =>
    setPickSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const unlink = () => { onChange(value.filter((id) => !sel.has(id))); setSel(new Set()); };
  const link = () => { onChange([...value, ...pickSel]); setPickSel(new Set()); setPickOpen(false); };

  return (
    <div className="sd-assoc">
      {/* Tab bar (single active tab) — mirrors the legacy XAF "Test Methods"
          child-collection tab. */}
      <div className="sd-assoc-tabbar">
        <span className="sd-assoc-tab active">
          <i className="ti ti-list-details" /> {field.label}
        </span>
      </div>
      <div className="sd-assoc-panel">
      <div className="sd-assoc-toolbar">
        {!readOnly && (
          <>
            <button type="button" className="ca-btn"
              onClick={() => { setPickSel(new Set()); setPickSearch(''); setPickOpen(true); }}>
              <span className="tb-i tb-i-green">＋</span> Link
            </button>
            <button type="button" className="ca-btn" disabled={sel.size === 0} onClick={unlink}>
              <span className="tb-i tb-i-red">✕</span> Unlink
            </button>
          </>
        )}
        {/* Shared "Export to" dropdown (all ALPACA formats) — exports the
            currently-linked rows, matching the legacy Test Methods toolbar. */}
        <ExportMenu
          filename={field.label.replace(/\s+/g, '')}
          headers={['TestName', 'MethodName', 'FormulaCount', 'IsFormulaActive']}
          rows={selectedRows.map((r) => [
            r.name, r.methodName ?? '', String(r.formulaCount ?? 0), r.isFormulaActive ? 'Yes' : 'No',
          ])}
          triggerClassName="ca-btn"
        />
      </div>
      <div className="sd-assoc-grid">
        <table>
          <thead><tr>
            {!readOnly && <th className="sd-assoc-cb" />}
            <th>TestName</th><th>MethodName</th><th>FormulaCount</th><th>IsFormulaActive</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="sd-assoc-empty">Loading…</td></tr>
            ) : selectedRows.length === 0 ? (
              <tr><td colSpan={5} className="sd-assoc-empty">No data to display</td></tr>
            ) : pageRows.map((r) => (
              <tr key={r.id}>
                {!readOnly && (
                  <td className="sd-assoc-cb">
                    <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} />
                  </td>
                )}
                <td>{r.name}</td><td>{r.methodName ?? ''}</td>
                <td>{r.formulaCount ?? 0}</td><td>{r.isFormulaActive ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer: Page X of N (items) · numbered pages · Page size (legacy parity). */}
      {selectedRows.length > 0 && (
        <div className="sd-assoc-foot">
          <span>Page {curPage} of {totalPages} ({selectedRows.length} items)</span>
          <span className="sd-assoc-pager">
            <button type="button" disabled={curPage <= 1}
              onClick={() => setPage(Math.max(1, curPage - 1))}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button type="button" key={n} className={n === curPage ? 'sd-assoc-pg-active' : ''}
                onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={curPage >= totalPages}
              onClick={() => setPage(Math.min(totalPages, curPage + 1))}>›</button>
          </span>
          <span className="sd-assoc-size">
            Page size:
            <select value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </span>
        </div>
      )}
      </div>

      {pickOpen && (
        <div className="sd-assoc-modal" role="dialog" aria-modal="true">
          <div className="sd-assoc-modal-box">
            <div className="sd-assoc-modal-head">
              Link Test Methods
              <button type="button" className="sd-assoc-x" onClick={() => setPickOpen(false)}>✕</button>
            </div>
            <input className="sd-input" placeholder="Search…" value={pickSearch}
              onChange={(e) => setPickSearch(e.target.value)} />
            <div className="sd-assoc-modal-list">
              <table>
                <thead><tr>
                  <th className="sd-assoc-cb" />
                  <th>TestName</th><th>MethodName</th><th>FormulaCount</th><th>IsFormulaActive</th>
                </tr></thead>
                <tbody>
                  {availFiltered.map((r) => (
                    <tr key={r.id} onClick={() => togglePick(r.id)} style={{ cursor: 'pointer' }}>
                      <td className="sd-assoc-cb">
                        <input type="checkbox" readOnly checked={pickSel.has(r.id)} />
                      </td>
                      <td>{r.name}</td><td>{r.methodName ?? ''}</td>
                      <td>{r.formulaCount ?? 0}</td><td>{r.isFormulaActive ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                  {availFiltered.length === 0 && (
                    <tr><td colSpan={5} className="sd-assoc-empty">No test methods</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="sd-assoc-modal-foot">
              <button type="button" className="ca-btn" disabled={pickSel.size === 0} onClick={link}>
                Link{pickSel.size ? ` (${pickSel.size})` : ''}
              </button>
              <button type="button" className="ca-btn" onClick={() => setPickOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Certificate-style file field (shared 'file' CrudFieldType). Two-phase upload:
 * picking a file POSTs it to `field.uploadUrl` and stores the returned FileData
 * Oid in the form (saved as the record's FK on Save); the filename renders as a
 * download link (GET `${downloadUrlBase}/${oid}/download`); Clear unsets it.
 * Mirrors the legacy XAF Certificate control (link + upload + clear).
 */
function FileField({ field, oid, fileName, readOnly, onChange }: {
  field: CrudField; oid: string; fileName: string; readOnly: boolean;
  /** (newOid, newFileName) — '' clears the attachment. */
  onChange: (oid: string, fileName: string) => void;
}) {
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const hasFile = oid !== '' && oid != null;

  const download = async () => {
    if (!hasFile || !field.downloadUrlBase) return;
    try {
      const res = await api.get(`${field.downloadUrlBase}/${oid}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'certificate';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Could not download the file.');
    }
  };

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || !field.uploadUrl) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ data?: { fileDataOid?: string; fileName?: string } }>(field.uploadUrl, fd);
      const newOid = res.data.data?.fileDataOid;
      if (!newOid) throw new Error('no oid');
      onChange(newOid, res.data.data?.fileName ?? file.name);
    } catch {
      notify.error('Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-row full">
      <label className="sd-label">{field.label}</label>
      <div className="sd-file">
        {hasFile ? (
          <button type="button" className="sd-file-link" onClick={download} title="Download">
            <i className="ti ti-file-text" /> {fileName || 'certificate'}
          </button>
        ) : (
          <span className="sd-file-none">N/A</span>
        )}
        {!readOnly && (
          <span className="sd-file-actions">
            <input ref={fileRef} type="file" hidden onChange={pick} />
            <button type="button" className="sd-file-btn" disabled={busy}
              onClick={() => fileRef.current?.click()} title={hasFile ? 'Replace' : 'Upload'}>
              <BusyIcon busy={busy} icon="ti-upload" /> {hasFile ? 'Replace' : 'Upload'}
            </button>
            {hasFile && (
              <button type="button" className="sd-file-btn danger" disabled={busy}
                onClick={() => onChange('', '')} title="Clear">
                <i className="ti ti-trash" /> Clear
              </button>
            )}
          </span>
        )}
      </div>
      {field.hint && <span className="sd-hint">{field.hint}</span>}
    </div>
  );
}
